import { formatDelayTime, formatRRule, formatTokenAmount } from "@/components/ui/workflow-step-row/utils/formatters";
import { formatAddress } from "@/lib/utils";
import { isSolanaAddress } from "@/lib/utils/address";

/**
 * Optional formatting context for smarter value display.
 * When provided, amounts are formatted with token decimals/symbol,
 * and delay durations are humanized.
 */
export interface DescriptionFormatHints {
  /** Token decimals for formatting raw amount values (e.g., 6 for USDC) */
  tokenDecimals?: number;
  /** Token symbol to append after formatted amounts (e.g., "USDC") */
  tokenSymbol?: string;
}

export interface ResolvedTemplateTextPart {
  type: "text";
  value: string;
}

export interface ResolvedTemplateFieldPart {
  type: "field";
  rawFieldName: string;
  fieldName: string;
  value: unknown;
  displayValue: string;
  matchedText: string;
  resolved: boolean;
}

export type ResolvedTemplatePart = ResolvedTemplateTextPart | ResolvedTemplateFieldPart;

/** Action types that always operate on the chain's native token (no tokenAddress field). */
export const NATIVE_ONLY_ACTION_TYPES = ["wrap-eth", "unwrap-eth", "send-native-token", "send-sol"] as const;

/** Payload fields that represent token amounts (raw units / wei). */
export const AMOUNT_FIELDS = new Set([
  // Common
  "amount",
  "amountIn",
  "sellAmount",
  "buyAmount",
  "targetPrice",
  "minAmount",
  // Uniswap LP
  "amountADesired",
  "amountBDesired",
  "amount0Desired",
  "amount1Desired",
  "amountAMin",
  "amountBMin",
  "amount0Min",
  "amount1Min",
  // Plume
  "depositAmountWei",
  "minAmountWei",
  // x402
  "maxAmountPerRequest",
  "maxPrice",
]);

/** Payload fields that represent durations in milliseconds. */
const DELAY_FIELDS = new Set(["delayMs", "retryIntervalMs", "timeoutMs", "timeout", "pollIntervalMs", "waitFor"]);

/** Payload fields that contain RRULE strings (cron/schedule definitions). */
const RRULE_FIELDS = new Set(["rrule"]);

/** Matches a full EVM address (0x + 40 hex chars). */
export const EVM_ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/;

/** Payload field names that are known to contain blockchain addresses. */
export const ADDRESS_FIELDS = new Set([
  "recipientAddress",
  "tokenAddress",
  "spenderAddress",
  "contractAddress",
  "walletAddress",
  "fromAddress",
  "toAddress",
  "senderAddress",
  "ownerAddress",
  "operatorAddress",
  "railgunAddress",
  "address",
  "to",
  "from",
  "owner",
  "spender",
  "operator",
  "recipient",
  "sender",
  "account",
  "vaultAddress",
  "poolAddress",
  "routerAddress",
  "factoryAddress",
  "positionManagerAddress",
  "tokenIn",
  "tokenOut",
  "token0",
  "token1",
  "tokenA",
  "tokenB",
  "sellToken",
  "buyToken",
  "currency0",
  "currency1",
]);

/**
 * Truncates a blockchain address for display: EVM (0x1234...5678), Solana (AbCd...wxyz).
 */
export function truncateAddress(value: string): string {
  if (EVM_ADDRESS_RE.test(value)) {
    return `${value.slice(0, 6)}...${value.slice(-4)}`;
  }
  // Solana base58 addresses (32-44 chars)
  if (isSolanaAddress(value)) {
    return formatAddress(value, 4, 4);
  }
  return value;
}

/**
 * Traverse a nested object by dot-separated path segments.
 * Returns undefined if any segment along the path is missing.
 */
function getNestedValue(obj: Record<string, unknown>, segments: string[]): unknown {
  let current: unknown = obj;
  for (const seg of segments) {
    if (current === null || current === undefined || typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[seg];
  }
  return current;
}

function formatResolvedTemplateValue(
  value: unknown,
  fieldName: string,
  matchedText: string,
  hints?: DescriptionFormatHints,
): { resolved: boolean; displayValue: string } {
  if (value === undefined || value === null) {
    return { resolved: false, displayValue: matchedText };
  }

  const strValue = String(value);

  // Simple arrays of primitives — join as comma-separated text
  if (Array.isArray(value)) {
    const primitives = value.filter(v => v !== null && v !== undefined && typeof v !== "object");
    if (primitives.length === 0) {
      return { resolved: false, displayValue: matchedText };
    }
    return { resolved: true, displayValue: primitives.join(", ") };
  }

  // Skip workflow expressions — they contain {{...}} refs
  // that aren't resolved until runtime. Show the leaf field name
  // instead of the raw template syntax for readability.
  if (strValue.includes("{{")) {
    return { resolved: true, displayValue: fieldName };
  }

  // Skip non-array objects — not displayable as inline text.
  // Show the leaf field name for readability.
  if (typeof value === "object") {
    return { resolved: true, displayValue: fieldName };
  }

  // Format amount fields with token decimals when available
  if (AMOUNT_FIELDS.has(fieldName) && hints?.tokenDecimals !== undefined && strValue !== "") {
    const formatted = formatTokenAmount(strValue, hints.tokenDecimals);
    if (hints.tokenSymbol) return { resolved: true, displayValue: `${formatted} ${hints.tokenSymbol}` };
    return { resolved: true, displayValue: formatted };
  }

  // Format delay fields as human-readable duration
  if (DELAY_FIELDS.has(fieldName)) {
    const ms = Number(value);
    if (!isNaN(ms) && ms > 0) {
      return { resolved: true, displayValue: formatDelayTime(ms) };
    }
  }

  // Format RRULE fields as human-readable schedule with local timezone
  if (RRULE_FIELDS.has(fieldName)) {
    return { resolved: true, displayValue: formatRRule(strValue) };
  }

  // Truncate EVM addresses for readability (0x1234...5678)
  if (ADDRESS_FIELDS.has(fieldName)) {
    return { resolved: true, displayValue: truncateAddress(strValue) };
  }

  return { resolved: true, displayValue: strValue };
}

export function resolveDescriptionTemplateParts(
  text: string | undefined,
  payload: Record<string, unknown> | undefined,
  hints?: DescriptionFormatHints,
): ResolvedTemplatePart[] {
  if (!text) return [];
  if (!payload) return [{ type: "text", value: text }];

  const parts: ResolvedTemplatePart[] = [];
  const regex = /\{payload\.([^}]+)\}/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    const [matchedText, rawFieldName] = match;
    if (match.index > lastIndex) {
      parts.push({ type: "text", value: text.slice(lastIndex, match.index) });
    }

    // Handle array index access on the last segment like "data.coinIds[0]"
    const indexMatch = rawFieldName.match(/^(.+)\[(\d+)\]$/);
    const pathWithoutIndex = indexMatch ? indexMatch[1] : rawFieldName;
    const arrayIndex = indexMatch ? parseInt(indexMatch[2], 10) : undefined;
    const segments = pathWithoutIndex.split(".");
    const fieldName = segments[segments.length - 1];

    const baseValue = segments.length === 1 ? payload[fieldName] : getNestedValue(payload, segments);
    if (arrayIndex !== undefined && Array.isArray(baseValue)) {
      const indexedValue = baseValue[arrayIndex];
      if (indexedValue === undefined || indexedValue === null || typeof indexedValue === "object") {
        parts.push({
          type: "field",
          rawFieldName,
          fieldName,
          value: indexedValue,
          displayValue: matchedText,
          matchedText,
          resolved: false,
        });
        lastIndex = match.index + matchedText.length;
        continue;
      }
    }

    const value =
      arrayIndex !== undefined && Array.isArray(baseValue) ? (baseValue[arrayIndex] ?? undefined) : baseValue;
    const { resolved, displayValue } = formatResolvedTemplateValue(value, fieldName, matchedText, hints);

    parts.push({
      type: "field",
      rawFieldName,
      fieldName,
      value,
      displayValue,
      matchedText,
      resolved,
    });

    lastIndex = match.index + matchedText.length;
  }

  if (lastIndex < text.length) {
    parts.push({ type: "text", value: text.slice(lastIndex) });
  }

  return parts;
}

/**
 * Resolves {payload.fieldName} template variables in AI-generated descriptions.
 *
 * The AI generates descriptions with template references like:
 *   "Transfers {payload.amount} {payload.symbol} to {payload.recipientAddress}"
 *
 * This function replaces them with actual values from the node's payload:
 *   "Transfers 2 USDC to 0xabc...1234"
 *
 * Supports:
 * - Simple fields: {payload.channel} → "general"
 * - Nested fields: {payload.data.from} → "0xabc" (dot-path traversal)
 * - Array fields: {payload.coinIds} → "ethereum, bitcoin" (joined)
 * - Array index: {payload.coinIds[0]} → "ethereum"
 *
 * Skips resolution when the payload value is:
 * - A workflow expression (e.g., "{{node.result.field}}")
 * - A non-array object (not displayable as inline text)
 * - Missing or null (template variable left as-is)
 *
 * When format hints are provided:
 * - Amount fields are converted from raw units to human-readable (e.g., 2000000 → "2 USDC")
 * - Delay fields are converted from ms to human-readable (e.g., 7200000 → "2h")
 * - RRULE fields are converted to human-readable schedule in local timezone
 */
export function resolveDescriptionTemplates(
  text: string | undefined,
  payload: Record<string, unknown> | undefined,
  hints?: DescriptionFormatHints,
): string | undefined {
  if (!text) return text;

  return resolveDescriptionTemplateParts(text, payload, hints)
    .map(part => (part.type === "text" ? part.value : part.displayValue))
    .join("");
}
